import { ApiClientBase } from '@@/api/api';
import { ApiResult } from '@@/types/ApiResult';
import type {
  CreateCaseInput,
  UpdateCaseInput,
  CaseResponse,
  ListCasesResponse,
  ListCasesParams,
  TemplateResponse,
  CreateTemplateInput,
  TemplateCreateResponse,
  TemplateDetailResponse,
  CaseAttachmentsResponse,
  AttachmentDownloadResponse,
  CaseCommentsResponse,
  CommentSubmitResponse,
  ProfileResponse,
  ListAgentsResponse,
  ListQueuesResponse,
  ListCompaniesResponse,
  CreateProfileParams
} from '../types/api.types';

// ============= Request Params Types =============
export interface GetCaseByIdParams {
  caseId: string;
  echoFields?: string[];
}

export interface ChangeCaseStatusParams {
  caseId: string;
  status: string;
  echoFields?: string[];
}

export interface GetCasesByEmailParams {
  company_name?: string;
  customer_email?: string;
  limit?: number;
  next_token?: string;
  status?: string;
  reference_number?: string;
  title?: string;
  agent_name?: string;
  queue_name?: string;
  date_from?: string;
  date_to?: string;
  timezone?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  case_type?: string;
}

export interface GetAllTemplatesParams {
  limit?: number;
  next_token?: string;
}

export interface UpdateTemplateParams {
  templateId: string;
  name?: string;
  description?: string | null;
  status?: 'Active' | 'Inactive';
}

export interface UploadCaseAttachmentParams {
  caseId: string;
  attachment: File;
}

export interface GetCaseCommentsParams {
  caseId: string;
  currentToken?: string;
  limit?: number;
}

export interface SubmitCaseCommentParams {
  caseId: string;
  commentBody: Record<string, string>;
}

export interface GetAgentListParams {
  limit?: number;
  next_token?: string;
  name?: string;
  queue_id?: string;
}

export interface GetQueuesListParams {
  limit?: number;
  next_token?: string;
  name?: string;
  queue_type?: string;
}

export interface GetCompaniesListParams {
  limit?: number;
  next_token?: string;
  name?: string;
  companyId?: string;
}

export interface GetProfileByCompanyParams {
  companyId: string;
  name?: string;
  limit?: number;
  next_token?: string;
}

export interface CompanyParams {
  companyId?: string;
  company_name: string;
  email: string;
  phone_number: string;
  website?: string;
  address_line1: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  description?: string;
}

export interface BulkChangeCaseStatusParams {
  case_ids: string[];
  fields: {
    status: string;
  };
}

export class CasesApiClient extends ApiClientBase {
  private normalizePaginatedResponse<T>(jsonResponse: Record<string, unknown>): ApiResult<T> {
    if (jsonResponse.ok && jsonResponse.data) {
      return jsonResponse as unknown as ApiResult<T>;
    }

    if (jsonResponse.success === true && 'data' in jsonResponse && 'pagination' in jsonResponse) {
      return {
        ok: true,
        data: {
          ...(jsonResponse.data as object),
          pagination: jsonResponse.pagination
        }
      } as ApiResult<T>;
    }

    return {
      ok: true,
      data: jsonResponse
    } as ApiResult<T>;
  }

  async createCase(payload: CreateCaseInput): Promise<ApiResult<CaseResponse>> {
    try {
      const headers = await this.getHeaders();

      const body: Record<string, unknown> = {
        fields: {
          Title: payload.title,
          Status: payload.status || 'open',
          'Customer Email': payload.customerEmail,
          ...(payload.customerId ? { 'Customer ID': payload.customerId } : {}),
          Summary: payload.summary,
          Priority: payload.priority || 'Medium',
          'Case Type': payload.caseType,
          ...(payload.assignedQueue ? { 'assigned queue': payload.assignedQueue } : {}),
          ...(payload.assignedUser ? { 'assigned user': payload.assignedUser } : {}),
          ...(payload.assignedQueueName ? { prnx_queue_name: payload.assignedQueueName } : {}),
          ...(payload.assignedAgentName ? { prnx_agent_name: payload.assignedAgentName } : {}),
          ...(payload.company ? { 'Company Id': payload.company } : {}),
          ...(payload.companyName ? { 'Company Name': payload.companyName } : {}),
          ...(payload.ccEmails ? { 'CC Email': payload.ccEmails } : {}),
          // Spread any additional custom fields
          ...Object.keys(payload).reduce(
            (acc, key) => {
              if (
                ![
                  'title',
                  'status',
                  'customerEmail',
                  'customerId',
                  'summary',
                  'priority',
                  'echoFields',
                  'assignedQueue',
                  'assignedQueueName',
                  'assignedUser',
                  'assignedAgentName',
                  'caseType',
                  'company',
                  'companyName',
                  'attachments',
                  'ccEmails'
                ].includes(key)
              ) {
                acc[key] = payload[key];
              }
              return acc;
            },
            {} as Record<string, unknown>
          )
        },
        echo_fields: payload.echoFields || ['Title', 'Status', 'Customer ID']
      };

      if (payload.attachments && Array.isArray(payload.attachments) && payload.attachments.length > 0) {
        body.attachments = payload.attachments;
      }

      const url = this.getCasesApiUrl('/support/cases');
      const result = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!result.ok) {
        return this.error(`Failed to create case: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to create case');
    }
  }

  async updateCase(payload: UpdateCaseInput): Promise<ApiResult<CaseResponse>> {
    try {
      const headers = await this.getHeaders();

      const body: Record<string, unknown> = {
        fields: {
          'CC Email': payload.ccEmails,
          ...(payload.title ? { Title: payload.title } : {}),
          ...(payload.status ? { Status: payload.status } : {}),
          ...(payload.customerEmail ? { 'Customer Email': payload.customerEmail } : {}),
          ...(payload.summary ? { Summary: payload.summary } : {}),
          ...(payload.priority ? { Priority: payload.priority } : {}),
          ...(payload.assignedQueue ? { 'assigned queue': payload.assignedQueue } : {}),
          ...(payload.assignedUser ? { 'assigned user': payload.assignedUser } : {}),
          ...(payload.assignedQueueName ? { prnx_queue_name: payload.assignedQueueName } : {}),
          ...(payload.assignedAgentName ? { prnx_agent_name: payload.assignedAgentName } : {}),
          ...(payload.caseType ? { 'Case Type': payload.caseType } : {}),
          // Spread any additional custom fields
          ...Object.keys(payload).reduce(
            (acc, key) => {
              if (
                ![
                  'caseId',
                  'title',
                  'status',
                  'customerEmail',
                  'summary',
                  'priority',
                  'echoFields',
                  'assignedQueue',
                  'assignedUser',
                  'assignedQueueName',
                  'assignedAgentName',
                  'caseType',
                  'ccEmails'
                ].includes(key)
              ) {
                acc[key] = payload[key];
              }
              return acc;
            },
            {} as Record<string, unknown>
          )
        },
        echo_fields: payload.echoFields || ['Title', 'Status']
      };

      const url = this.getCasesApiUrl(`/support/cases/${payload.caseId}`);
      const result = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body)
      });

      if (!result.ok) {
        return this.error(`Failed to update case: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to update case');
    }
  }

  async getCaseById(params: GetCaseByIdParams): Promise<ApiResult<CaseResponse>> {
    try {
      const headers = await this.getHeaders();

      const url = this.getCasesApiUrl(`/support/cases/${params.caseId}`);
      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch case: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch case');
    }
  }

  async listCases(params?: ListCasesParams): Promise<ApiResult<ListCasesResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParamsObj = {
        ...(params?.limit ? { maxResults: params.limit } : {}),
        ...(params?.status && params.status !== 'All' ? { statuses: params.status } : {}),
        ...(params?.reference_number ? { reference_number: params.reference_number } : {}),
        ...(params?.title ? { title: params.title } : {}),
        ...(params?.agent_name ? { agent_name: params.agent_name } : {}),
        ...(params?.queue_name ? { queue_name: params.queue_name } : {}),
        ...(params?.date_from ? { date_from: params.date_from } : {}),
        ...(params?.date_to ? { date_to: params.date_to } : {}),
        ...(params?.company_name ? { company_name: params.company_name } : {}),
        ...(params?.timezone ? { timezone: params.timezone } : {}),
        ...(params?.sort_by ? { sort_by: params.sort_by } : {}),
        ...(params?.sort_order ? { sort_order: params.sort_order } : {}),
        ...(params?.next_token ? { nextToken: params.next_token } : {}),
        ...(params?.case_type ? { case_types: params.case_type } : {}),
        ...(params?.reporter_name ? { reporter_name: params.reporter_name } : {})
      };

      const queryParams = this.formatQueryStrings(queryParamsObj);
      const url = this.getCasesApiUrl(`/support/cases${queryParams ? `?${queryParams}` : ''}`);

      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to list cases: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<ListCasesResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to list cases');
    }
  }

  async getCasesByEmail(params?: GetCasesByEmailParams): Promise<ApiResult<ListCasesResponse>> {
    try {
      const headers = await this.getHeaders();

      const queryParamsObj = {
        ...(params?.company_name ? { company_name: params.company_name } : {}),
        ...(params?.status && params.status !== 'All' ? { statuses: params.status } : {}),
        ...(params?.reference_number ? { reference_number: params.reference_number } : {}),
        ...(params?.title ? { title: params.title } : {}),
        ...(params?.agent_name ? { agent_name: params.agent_name } : {}),
        ...(params?.queue_name ? { queue_name: params.queue_name } : {}),
        ...(params?.date_from ? { date_from: params.date_from } : {}),
        ...(params?.date_to ? { date_to: params.date_to } : {}),
        ...(params?.limit ? { maxResults: params.limit } : {}),
        ...(params?.timezone ? { timezone: params.timezone } : {}),
        ...(params?.sort_by ? { sort_by: params.sort_by } : {}),
        ...(params?.sort_order ? { sort_order: params.sort_order } : {}),
        ...(params?.next_token ? { nextToken: params.next_token } : {}),
        ...(params?.case_type ? { case_types: params.case_type } : {})
      };

      const queryParams = this.formatQueryStrings(queryParamsObj);
      const url = this.getCasesApiUrl(
        `support/cases/customer/${params?.customer_email}${queryParams ? `?${queryParams}` : ''}`
      );

      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch cases by email: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<ListCasesResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch cases by email');
    }
  }

  async changeCaseStatus(params: ChangeCaseStatusParams): Promise<ApiResult<CaseResponse>> {
    try {
      const headers = await this.getHeaders();

      const body = {
        fields: { Status: params.status },
        echo_fields: params.echoFields || ['Title', 'Status', 'Customer ID']
      };

      const url = this.getCasesApiUrl(`/support/cases/${params.caseId}`);
      const result = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body)
      });

      if (!result.ok) {
        return this.error(`Failed to change case status: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to change case status');
    }
  }

  async bulkChangeCaseStatus(params: BulkChangeCaseStatusParams): Promise<ApiResult<CaseResponse>> {
    try {
      const headers = await this.getHeaders();

      const url = this.getCasesApiUrl('/support/cases/bulk-update');
      const result = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(params)
      });

      if (!result.ok) {
        return this.error(`Failed to change case status: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to change case status');
    }
  }
  // ============= Templates Operations =============
  async getAllTemplates(params?: GetAllTemplatesParams): Promise<ApiResult<TemplateResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings({
        maxResults: params?.limit,
        nextToken: params?.next_token
      });

      const url = this.getCasesApiUrl(`/support/case-templates/templates${queryParams ? `?${queryParams}` : ''}`);
      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch templates: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<TemplateResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch templates');
    }
  }

  async createTemplate(payload: CreateTemplateInput): Promise<ApiResult<TemplateCreateResponse>> {
    try {
      const headers = await this.getHeaders();

      const url = this.getCasesApiUrl('/support/case-templates/templates');
      const result = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!result.ok) {
        return this.error(`Failed to create template: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to create template');
    }
  }

  async updateTemplate(payload: CreateTemplateInput, templateId: string): Promise<ApiResult<TemplateDetailResponse>> {
    try {
      const headers = await this.getHeaders();

      const url = this.getCasesApiUrl(`/support/case-templates/templates/${templateId}`);
      const result = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });

      if (!result.ok) {
        return this.error(`Failed to update template: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to update template');
    }
  }

  async getTemplateById(templateId: string): Promise<ApiResult<TemplateDetailResponse>> {
    try {
      const headers = await this.getHeaders();
      const url = this.getCasesApiUrl(`/support/case-templates/templates/${templateId}`);

      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch template: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch template');
    }
  }

  // ============= Attachments Operations =============

  async uploadCaseAttachment(params: UploadCaseAttachmentParams): Promise<ApiResult<CaseAttachmentsResponse>> {
    try {
      const headers = await this.getHeaders();
      if ('content-type' in headers) delete (headers as Record<string, string>)['content-type'];
      const formData = new FormData();
      formData.append('file', params.attachment);

      const url = this.getCasesApiUrl(`/support/cases/${params.caseId}/attachments`);
      const result = await fetch(url, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!result.ok) {
        return this.error(`Failed to upload attachment: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to upload attachment');
    }
  }

  async getCaseAttachments(caseId: string): Promise<ApiResult<CaseAttachmentsResponse>> {
    try {
      const headers = await this.getHeaders();
      const url = this.getCasesApiUrl(`/support/cases/${caseId}/attachments`);

      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch attachments: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch attachments');
    }
  }

  async downloadCaseAttachment(downloadEndpoint: string): Promise<ApiResult<AttachmentDownloadResponse>> {
    try {
      const headers = await this.getHeaders();
      const url = this.getCasesApiUrl(`/${downloadEndpoint}`);

      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to download attachment: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to download attachment');
    }
  }

  // ============= Comments Operations =============

  async getCaseComments(params: GetCaseCommentsParams): Promise<ApiResult<CaseCommentsResponse>> {
    try {
      const headers = await this.getHeaders();

      const queryParams = this.formatQueryStrings({
        maxResults: params.limit || 50,
        ...(params.currentToken ? { next_token: params.currentToken } : {})
      });

      const url = this.getCasesApiUrl(
        `/support/cases/${params.caseId}/comments${queryParams ? `?${queryParams}` : ''}`
      );
      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch comments: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<CaseCommentsResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch comments');
    }
  }

  async submitCaseComment(params: SubmitCaseCommentParams): Promise<ApiResult<CommentSubmitResponse>> {
    try {
      const headers = await this.getHeaders();

      const url = this.getCasesApiUrl(`/support/cases/${params.caseId}/comments`);
      const result = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(params.commentBody)
      });

      if (!result.ok) {
        return this.error(`Failed to submit comment: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to submit comment');
    }
  }

  // ============= Customer Profile Operations =============

  async getProfile(customerId: string): Promise<ApiResult<ProfileResponse>> {
    try {
      const headers = await this.getHeaders();
      const url = this.getCasesApiUrl(`/support/profile/${customerId}`);

      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch profile: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch profile');
    }
  }

  async getProfileByCompany(params: GetProfileByCompanyParams): Promise<ApiResult<ProfileResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings({
        accountNumber: params.companyId,
        name: params.name,
        maxResults: params.limit,
        next_token: params.next_token
      });

      const url = this.getCasesApiUrl(`/support/profile${queryParams ? `?${queryParams}` : ''}`);
      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch profile by company: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<ProfileResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch profile by company');
    }
  }

  async createProfile(params: CreateProfileParams): Promise<ApiResult<ProfileResponse>> {
    try {
      const headers = await this.getHeaders();
      const url = this.getCasesApiUrl('/support/profile');

      const result = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(params)
      });

      if (!result.ok) {
        return this.error(`Failed to fetch profile: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch profile');
    }
  }
  async updateProfile(params: CreateProfileParams, profileId: string): Promise<ApiResult<ProfileResponse>> {
    try {
      const headers = await this.getHeaders();
      const url = this.getCasesApiUrl(`/support/profile/${profileId}`);

      const result = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(params)
      });

      if (!result.ok) {
        return this.error(`Failed to update profile: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }

  // ============= Agents & Queues Operations =============

  async getAgentList(params?: GetAgentListParams): Promise<ApiResult<ListAgentsResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings({
        limit: params?.limit,
        next_token: params?.next_token,
        name: params?.name,
        queue_id: params?.queue_id
      });

      const url = this.getCasesApiUrl(`/support/connect/agents${queryParams ? `?${queryParams}` : ''}`);
      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch agents: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<ListAgentsResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch agents');
    }
  }

  async getQueuesList(params?: GetQueuesListParams): Promise<ApiResult<ListQueuesResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings({
        limit: params?.limit,
        next_token: params?.next_token,
        name: params?.name,
        queue_type: params?.queue_type
      });

      const url = this.getCasesApiUrl(`/support/connect/queues${queryParams ? `?${queryParams}` : ''}`);
      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch queues: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<ListQueuesResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch queues');
    }
  }

  async getCompaniesList(params?: GetCompaniesListParams): Promise<ApiResult<ListCompaniesResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings({
        limit: params?.limit,
        next_token: params?.next_token,
        company_name: params?.name,
        company_id: params?.companyId
      });

      const url = this.getCasesApiUrl(`/support/companies${queryParams ? `?${queryParams}` : ''}`);
      const result = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error(`Failed to fetch companies: ${result.status} ${result.statusText}`);
      }

      const jsonResponse = await result.json();
      return this.normalizePaginatedResponse<ListCompaniesResponse>(jsonResponse);
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch companies');
    }
  }

  async addCompany(params?: CompanyParams): Promise<ApiResult<ListCompaniesResponse>> {
    try {
      const headers = await this.getHeaders();

      const url = this.getCasesApiUrl(`/support/companies`);
      const result = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(params)
      });

      if (!result.ok) {
        return this.error(`Failed to fetch companies: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to fetch companies');
    }
  }
  async updateCompany(companyId: string, params: CompanyParams): Promise<ApiResult<ListCompaniesResponse>> {
    try {
      const headers = await this.getHeaders();

      const url = this.getCasesApiUrl(`/support/companies/${companyId}`);
      const result = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(params)
      });

      if (!result.ok) {
        return this.error(`Failed to update company: ${result.status} ${result.statusText}`);
      }

      return await result.json();
    } catch (error) {
      return this.error(error instanceof Error ? error.message : 'Failed to update company');
    }
  }
}
